import {
	ACTIVE_PIPELINE_STAGES,
	CampaignPipelineStage,
	CampaignStatusTrackingData,
} from "@/api/campaignStatusTracking";

export interface StageDefinition {
	stage: CampaignPipelineStage;
	label: string;
	subLabel: string;
	color: string;
	isPipeline: boolean;
}

/** Stage labels / sub-labels from Campaign Progress Figma + PRD */
export const STAGE_DEFINITIONS: StageDefinition[] = [
	{
		stage: CampaignPipelineStage.PENDING_ACCEPTANCE,
		label: "Pending Acceptance",
		subLabel: "Awaiting creator acceptance",
		color: "#6936dd",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.CONCEPTING,
		label: "Concepting",
		subLabel: "Concepting & Review",
		color: "#4460ef",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.VIDEO_PRODUCTION,
		label: "Video Production",
		subLabel: "Filming & Review",
		color: "#4bc9f1",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.APPROVED,
		label: "Approved",
		subLabel: "For Posting",
		color: "#bb7f05",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.GO_LIVE,
		label: "Go Live",
		subLabel: "Posted Videos",
		color: "#f72586",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.PAYMENT_PROCESS,
		label: "Payment Process",
		subLabel: "Invoicing & Processing Creator Payment",
		color: "#eee533",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.DONE,
		label: "Done",
		subLabel: "Payment is released to creator",
		color: "#66b026",
		isPipeline: true,
	},
	{
		stage: CampaignPipelineStage.DROPPED,
		label: "Dropped",
		subLabel: "Dropped from campaign/CET cancelled",
		color: "#79626d",
		isPipeline: false,
	},
];

export const STAGE_BY_KEY = Object.fromEntries(STAGE_DEFINITIONS.map((def) => [def.stage, def])) as Record<
	CampaignPipelineStage,
	StageDefinition
>;

export interface DistributionSegment {
	stage: CampaignPipelineStage;
	count: number;
	widthPercent: number;
	color: string;
	label: string;
}

/**
 * Compute bar segment widths that round up and always sum to 100%.
 * Includes Dropped when present (Campaign Progress Figma).
 * Remainder is allocated to the largest segment (PRD §10 Q4).
 */
export function computeDistributionSegments(
	stageCounts: Record<CampaignPipelineStage, number>,
	totalRoster: number,
): DistributionSegment[] {
	const stagesWithCounts = STAGE_DEFINITIONS.map((def) => def.stage).filter((stage) => stageCounts[stage] > 0);
	const barTotal = stagesWithCounts.reduce((sum, stage) => sum + stageCounts[stage], 0);
	if (barTotal <= 0 || totalRoster < 0) return [];

	const rawPercents = stagesWithCounts.map((stage) => ({
		stage,
		count: stageCounts[stage],
		raw: (stageCounts[stage] / barTotal) * 100,
	}));

	let rounded = rawPercents.map((entry) => ({
		...entry,
		widthPercent: Math.ceil(entry.raw),
	}));

	const sum = rounded.reduce((acc, entry) => acc + entry.widthPercent, 0);
	if (sum !== 100 && rounded.length > 0) {
		const largestIdx = rounded.reduce(
			(bestIdx, entry, idx, arr) => (entry.count > arr[bestIdx].count ? idx : bestIdx),
			0,
		);
		rounded = rounded.map((entry, idx) =>
			idx === largestIdx ? { ...entry, widthPercent: entry.widthPercent + (100 - sum) } : entry,
		);
	}

	return rounded.map((entry) => ({
		stage: entry.stage,
		count: entry.count,
		widthPercent: entry.widthPercent,
		color: STAGE_BY_KEY[entry.stage].color,
		label: STAGE_BY_KEY[entry.stage].label,
	}));
}

/** Show only stages with at least one creator (Figma hides zero-count cards). */
export function getVisibleStageCards(data: CampaignStatusTrackingData): StageDefinition[] {
	const pipelineCards = ACTIVE_PIPELINE_STAGES.filter((stage) => data.stageCounts[stage] > 0).map(
		(stage) => STAGE_BY_KEY[stage],
	);

	if (data.stageCounts[CampaignPipelineStage.DROPPED] > 0) {
		pipelineCards.push(STAGE_BY_KEY[CampaignPipelineStage.DROPPED]);
	}

	return pipelineCards;
}

export function formatRelativeUpdatedAt(iso: string | null): string {
	if (!iso) return "Updated just now";

	const updated = new Date(iso).getTime();
	const now = Date.now();
	const diffMs = Math.max(0, now - updated);
	const diffMinutes = Math.floor(diffMs / 60_000);

	if (diffMinutes < 1) return "Updated just now";
	if (diffMinutes < 60) return `Updated ${diffMinutes}m ago`;

	const diffHours = Math.floor(diffMinutes / 60);
	if (diffHours < 24) return `Updated ${diffHours}h ago`;

	const diffDays = Math.floor(diffHours / 24);
	return `Updated ${diffDays}d ago`;
}

/** Figma meta: "Last updated 17 Aug 2026, 2:14 PM" */
export function formatLastUpdatedMeta(iso: string | null): string {
	if (!iso) return "Last updated just now";

	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "Last updated just now";

	const day = date.getDate();
	const month = date.toLocaleString("en-GB", { month: "short" });
	const year = date.getFullYear();
	const time = date.toLocaleString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});

	return `Last updated ${day} ${month} ${year}, ${time}`;
}

export function buildStageDistribution(data: CampaignStatusTrackingData): Record<string, number> {
	return ACTIVE_PIPELINE_STAGES.reduce<Record<string, number>>((acc, stage) => {
		acc[stage] = data.stageCounts[stage];
		return acc;
	}, {});
}
